'use client';

import { useState } from 'react';

import {
  Avatar,
  Badge,
  ColorDot,
  LinearProgress,
  Meter,
  Tooltip,
  PlusIcon,
} from '../atomic-atoms';
import { MainButton } from '../atomic-atoms/buttons/MainButton';
import { SecondaryButton } from '../atomic-atoms/buttons/SecondaryButton';
import { BarChart, DonutChart } from '../atomic-molecules/Charts';
import { ChatButton } from '../atomic-molecules/ChatButton';
import { DataBadge } from '../atomic-molecules/DataBadge';
import { EmptyState } from '../atomic-molecules/EmptyState';
import { ProgressMeter } from '../atomic-molecules/ProgressMeter';
import { RichTextEditor } from '../atomic-molecules/RichTextEditor';
import { SearchBar } from '../atomic-molecules/SearchBar';
import { SmartInput } from '../atomic-molecules/SmartInput/SmartInput';
import { ThemeToggle } from '../atomic-molecules/ThemeToggle';
import { ToggleSwitch } from '../atomic-molecules/ToggleSwitch';
import { OptionsMenu } from '../atomic-organisms/OptionsMenu';
import { PageTabs } from '../atomic-organisms/PageTabs/PageTabs';
import { SectionStepperTabs } from '../atomic-organisms/SectionStepperTabs/SectionStepperTabs';
import { SectionTabs } from '../atomic-organisms/SectionTabs/SectionTabs';
import { SpeedDialMenu } from '../atomic-organisms/SpeedDialMenu';
import { Stepper } from '../atomic-organisms/Stepper/Stepper';
import { InfiniteTable } from '../atomic-organisms/Tables/InfiniteTable';
import { PaginatedTable } from '../atomic-organisms/Tables/PaginatedTable';

import styles from './ComponentLibrary.module.scss';

type ExampleRow = {
  id: string;
  task: string;
  owner: string;
  status: string;
  hours: number;
};

const chartData = [
  { label: 'Mon', value: 2.4, color: 'var(--nn-color-primary-accent-light)' },
  { label: 'Tue', value: 3.2, color: 'var(--nn-accent-green)' },
  { label: 'Wed', value: 1.8, color: 'var(--nn-accent-blue)' },
  { label: 'Thu', value: 2.8, color: 'var(--nn-accent-orange)' },
];

const donutData = [
  { label: 'Design', value: 64, color: 'var(--nn-color-primary-accent-light)' },
  { label: 'Research', value: 22, color: 'var(--nn-accent-blue)' },
  { label: 'QA', value: 14, color: 'var(--nn-accent-green)' },
];

const tableRows: ExampleRow[] = [
  {
    id: 'task-1',
    task: 'Wireframe QA pass',
    owner: 'Marc Pitre',
    status: 'In Progress',
    hours: 2.5,
  },
  {
    id: 'task-2',
    task: 'Homepage polish',
    owner: 'Sherri Miles',
    status: 'Backlog',
    hours: 1,
  },
  {
    id: 'task-3',
    task: 'Launch checklist',
    owner: 'Arthur Chen',
    status: 'Completed',
    hours: 4,
  },
];

const tableColumns = [
  {
    id: 'task',
    header: 'Task',
    accessor: (row: ExampleRow) => row.task,
    sortable: true,
  },
  {
    id: 'owner',
    header: 'Owner',
    accessor: (row: ExampleRow) => row.owner,
    sortable: true,
  },
  {
    id: 'status',
    header: 'Status',
    accessor: (row: ExampleRow) => row.status,
    sortable: true,
    cell: (row: ExampleRow) => (
      <Badge label={row.status} tone="default" size="sm" />
    ),
  },
  {
    id: 'hours',
    header: 'Hours',
    align: 'right' as const,
    accessor: (row: ExampleRow) => row.hours,
    sortable: true,
  },
];

export function ComponentLibrary() {
  const [activeTab, setActiveTab] = useState('atoms');
  const [activePageTab] = useState('/dev/component-library');
  const [activeStep, setActiveStep] = useState(1);
  const [activeStepperSection, setActiveStepperSection] = useState('details');
  const [selectValue, setSelectValue] = useState('active');
  const [toggleValue, setToggleValue] = useState(true);

  return (
    <main className={styles.componentLibrary}>
      <header className={styles.componentLibrary__header}>
        <div>
          <p className={styles.componentLibrary__eyebrow}>Net Net UI</p>
          <h1>Component Library</h1>
          <p>
            Development-only gallery for reusable atoms, molecules, organisms,
            and composition patterns.
          </p>
        </div>
        <div className={styles.componentLibrary__headerActions}>
          <ThemeToggle />
          <Badge label="Development only" tone="warning" size="lg" />
        </div>
      </header>

      <section className={styles.componentLibrary__section}>
        <SectionHeader
          title="Atoms"
          description="Small primitives used across product surfaces."
        />
        <ExampleGrid>
          <ExampleCard title="Primary Button">
            <div className={styles.componentLibrary__inline}>
              <MainButton ariaLabel="Primary action">Create</MainButton>
              <MainButton ariaLabel="Loading action" isLoading>
                Saving
              </MainButton>
              <MainButton ariaLabel="Disabled action" disabled>
                Disabled
              </MainButton>
            </div>
          </ExampleCard>

          <ExampleCard title="Secondary Button">
            <div className={styles.componentLibrary__inline}>
              <SecondaryButton ariaLabel="Theme action">Theme</SecondaryButton>
              <SecondaryButton
                ariaLabel="Emphasis action"
                backgroundOverride="emphasis"
              >
                Emphasis
              </SecondaryButton>
              <SecondaryButton ariaLabel="Mini action" variant="mini">
                <PlusIcon />
              </SecondaryButton>
            </div>
          </ExampleCard>

          <ExampleCard title="Badges">
            <div className={styles.componentLibrary__inline}>
              <Badge label="Default" tone="default" size="md" />
              <Badge label="Success" tone="success" size="md" />
              <Badge label="Info" tone="info" size="md" />
              <Badge label="Warning" tone="warning" size="md" />
              <Badge label="Error" tone="error" size="md" />
            </div>
          </ExampleCard>

          <ExampleCard title="Avatar">
            <div className={styles.componentLibrary__inline}>
              <Avatar name="Marc Pitre" size="small" />
              <Avatar name="Sherri Miles" size="medium" />
              <Avatar name="Net Net" size="large" />
            </div>
          </ExampleCard>

          <ExampleCard title="ColorDot">
            <div className={styles.componentLibrary__legend}>
              <span>
                <ColorDot color="var(--nn-color-primary-accent-light)" />
                Primary
              </span>
              <span>
                <ColorDot color="var(--nn-accent-green)" />
                Green
              </span>
              <span>
                <ColorDot color="var(--nn-accent-blue)" />
                Blue
              </span>
            </div>
          </ExampleCard>

          <ExampleCard title="LinearProgress">
            <div className={styles.componentLibrary__linearProgressExample}>
              <LinearProgress>Loading component state</LinearProgress>
            </div>
          </ExampleCard>

          <ExampleCard title="Meter">
            <div className={styles.componentLibrary__stack}>
              <Meter
                percentage={64}
                label="LOE 6.4 / 10h"
                ariaLabel="LOE meter"
              />
              <Meter
                percentage={92}
                label="Jun 14 - Overdue"
                ariaLabel="Timeline meter"
              />
            </div>
          </ExampleCard>

          <ExampleCard title="Tooltip">
            <Tooltip title="Helpful context without cluttering the UI">
              <SecondaryButton ariaLabel="Tooltip example">
                Hover for tooltip
              </SecondaryButton>
            </Tooltip>
          </ExampleCard>
        </ExampleGrid>
      </section>

      <section className={styles.componentLibrary__section}>
        <SectionHeader
          title="Molecules"
          description="Form controls, compact data cards, charts, and editor inputs."
        />
        <ExampleGrid>
          <ExampleCard title="SmartInput">
            <div className={styles.componentLibrary__stack}>
              <SmartInput
                name="component-library-text"
                label="Task name"
                type="text"
                defaultValue="Wireframe QA pass"
              />
              <SmartInput
                name="component-library-select"
                label="Status"
                type="select"
                value={selectValue}
                onChange={setSelectValue}
                options={[
                  { label: 'Active', value: 'active' },
                  { label: 'Completed', value: 'completed' },
                  { label: 'Archived', value: 'archived' },
                ]}
              />
              <SmartInput
                name="component-library-textarea"
                label="Notes"
                type="textarea"
                defaultValue="Useful for checking field spacing and labels."
                minRows={3}
              />
            </div>
          </ExampleCard>

          <ExampleCard title="SearchBar">
            <SearchBar
              placeholder="Search components..."
              onDebouncedChange={() => undefined}
            />
          </ExampleCard>

          <ExampleCard title="ToggleSwitch">
            <ToggleSwitch
              checked={toggleValue}
              onChange={setToggleValue}
              label="Chat notifications"
              helperText="Used for binary settings."
            />
          </ExampleCard>

          <ExampleCard title="ChatButton">
            <div className={styles.componentLibrary__inline}>
              <ChatButton />
              <ChatButton unreadCount={3} />
              <ChatButton messageCount={8} unreadCount={4} mentionCount={2} />
              <ChatButton disabled />
            </div>
          </ExampleCard>

          <ExampleCard title="DataBadge">
            <div className={styles.componentLibrary__badgeGrid}>
              <DataBadge title="Today" value="3.2 hrs" description="Logged" />
              <DataBadge
                title="This week"
                value="12 hrs"
                description="75% capacity"
                variant="info"
              />
              <DataBadge
                title="Blocked"
                value="2"
                description="Needs attention"
                variant="warning"
              />
            </div>
          </ExampleCard>

          <ExampleCard title="ProgressMeter">
            <div className={styles.componentLibrary__stack}>
              <ProgressMeter
                percentage={75}
                label="120 hrs / 160 hrs"
                rightLabel="75%"
                tone="default"
              />
              <ProgressMeter
                percentage={42}
                label="Tasks complete"
                rightLabel="42%"
                tone="success"
              />
              <ProgressMeter
                percentage={88}
                label="Risk"
                rightLabel="88%"
                tone="warning"
              />
            </div>
          </ExampleCard>

          <ExampleCard title="EmptyState">
            <EmptyState
              title="No components selected"
              description="Use this when a collection has no rows or matching filters."
              actions={
                <SecondaryButton ariaLabel="Reset filters">
                  Reset filters
                </SecondaryButton>
              }
            />
          </ExampleCard>

          <ExampleCard title="Charts">
            <div className={styles.componentLibrary__charts}>
              <BarChart data={chartData} height={180} valueLabel="Hours" />
              <DonutChart
                data={donutData}
                centerLabel="120"
                centerSubLabel="hrs"
                height={180}
              />
            </div>
          </ExampleCard>

          <ExampleCard title="RichTextEditor" wide>
            <RichTextEditor
              placeholder="Write a message..."
              footerLeft="[[ work references   @ people"
              footerRight="Top-level message"
              onSubmit={() => undefined}
            />
          </ExampleCard>

          <ExampleCard title="RichTextEditor Variants" wide>
            <div className={styles.componentLibrary__editorVariants}>
              <RichTextEditor
                placeholder="Compact editor with limited formatting..."
                footerLeft="Limited toolbar"
                footerRight="Enter to submit"
                showSubmitButton
                formattingOptions={['bold', 'italic', 'link', 'bulletList']}
                onSubmit={() => undefined}
              />
              <RichTextEditor
                value='<p>This read-only editor can display saved rich text with <a href="https://netnet.app">links</a>.</p>'
                footerLeft="Read-only"
                showToolbar={false}
                readOnly
              />
              <RichTextEditor
                placeholder="Custom action slot"
                footerLeft="Custom action"
                footerRight="No default submit"
                actionSlot={
                  <SecondaryButton ariaLabel="Custom editor action">
                    Custom
                  </SecondaryButton>
                }
              />
            </div>
          </ExampleCard>
        </ExampleGrid>
      </section>

      <section className={styles.componentLibrary__section}>
        <SectionHeader
          title="Organisms"
          description="Composed controls and navigational pieces."
        />
        <ExampleGrid>
          <ExampleCard title="PageTabs" wide>
            <PageTabs
              activeHref={activePageTab}
              tabs={[
                { label: 'My Tasks', href: '/dev/component-library' },
                { label: 'My Lists', href: '/dev/component-library/lists' },
                { label: 'My Time', href: '/dev/component-library/time' },
                {
                  label: 'Loading',
                  href: '/dev/component-library/loading',
                  isLoading: true,
                },
                {
                  label: 'Disabled',
                  href: '/dev/component-library/disabled',
                  disabled: true,
                  tooltip: 'Disabled tab tooltip',
                },
              ]}
              wrap
            />
          </ExampleCard>

          <ExampleCard title="SectionTabs">
            <SectionTabs
              sections={[
                { id: 'atoms', label: 'Atoms' },
                { id: 'molecules', label: 'Molecules' },
                { id: 'organisms', label: 'Organisms' },
                { id: 'disabled', label: 'Disabled', disabled: true },
              ]}
              activeId={activeTab}
              onSectionChange={setActiveTab}
            />
          </ExampleCard>

          <ExampleCard title="SectionStepperTabs">
            <SectionStepperTabs
              sections={[
                { id: 'summary', label: 'Summary', completed: true },
                { id: 'details', label: 'Details' },
                { id: 'review', label: 'Review', disabled: true },
              ]}
              activeId={activeStepperSection}
              onSectionChange={setActiveStepperSection}
              navigationMode="sequential"
              aria-label="Component library sections"
            />
          </ExampleCard>

          <ExampleCard title="Stepper" wide>
            <Stepper
              steps={[
                {
                  label: 'Summary',
                  content: <p>Collect the core details.</p>,
                },
                {
                  label: 'Details',
                  content: <p>Configure reusable component behavior.</p>,
                },
                {
                  label: 'Review',
                  content: <p>Confirm before shipping.</p>,
                },
              ]}
              activeStep={activeStep}
              onStepChange={setActiveStep}
              linear={false}
              variant="horizontal"
            />
          </ExampleCard>

          <ExampleCard title="OptionsMenu">
            <OptionsMenu
              ariaLabel="Component actions"
              actions={[
                {
                  key: 'edit',
                  label: 'Edit',
                  onSelect: () => undefined,
                },
                {
                  key: 'duplicate',
                  label: 'Duplicate',
                  onSelect: () => undefined,
                },
                {
                  key: 'delete',
                  label: 'Delete',
                  tone: 'danger',
                  onSelect: () => undefined,
                },
              ]}
            />
          </ExampleCard>

          <ExampleCard title="SpeedDialMenu">
            <div className={styles.componentLibrary__speedDialExample}>
              <SpeedDialMenu
                ariaLabel="Component speed dial"
                persistActionTooltips
                actions={[
                  {
                    key: 'add',
                    label: 'Add',
                    icon: <PlusIcon />,
                    onClick: () => undefined,
                  },
                  {
                    key: 'edit',
                    label: 'Edit',
                    icon: <span>E</span>,
                    onClick: () => undefined,
                  },
                ]}
              />
            </div>
          </ExampleCard>

          <ExampleCard title="PaginatedTable" wide>
            <PaginatedTable
              tableId="component-library-paginated-table"
              columns={tableColumns}
              rows={tableRows}
              getRowId={(row) => row.id}
              page={1}
              pageSize={5}
              totalRows={tableRows.length}
              onPageChange={() => undefined}
              onPageSizeChange={() => undefined}
              density="compact"
            />
          </ExampleCard>

          <ExampleCard title="InfiniteTable" wide>
            <InfiniteTable
              tableId="component-library-infinite-table"
              columns={tableColumns}
              rows={tableRows}
              getRowId={(row) => row.id}
              onLoadMore={() => undefined}
              hasMore={false}
              density="compact"
              virtualizedHeight="auto"
            />
          </ExampleCard>
        </ExampleGrid>
      </section>
    </main>
  );
}

function SectionHeader({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className={styles.componentLibrary__sectionHeader}>
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
  );
}

function ExampleGrid({ children }: { children: React.ReactNode }) {
  return <div className={styles.componentLibrary__grid}>{children}</div>;
}

function ExampleCard({
  title,
  children,
  wide = false,
}: {
  title: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <article
      className={[
        styles.componentLibrary__card,
        wide ? styles.componentLibrary__cardWide : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <h3>{title}</h3>
      <div className={styles.componentLibrary__example}>{children}</div>
    </article>
  );
}
